import type { ReactNode } from 'react';
import { Fragment } from 'react';

import type { ApiListingLocationType } from '#/api/types/ApiListingLocationType';
import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { Box } from '#/components/Box/Box';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import type { ApiVolopPromotionRadius } from '#/modules/listing/volops/api/types/ApiVolopPromotionRadius';
import { SearchHitInfoBoxListingLabels } from '#/modules/search/components/Hit/Info/ListingLabels/SearchHitInfoBoxListingLabels';
import {
  SearchHitInfoFooterHolder,
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
  SearchHitInfoPipe,
  SearchHitInfoPromotedLabel,
  SearchHitInfoStats,
} from '#/modules/search/components/Hit/Info/SearchHitInfo.styled';
import { SearchHitApplicationStatus } from '#/modules/search/components/Hit/SearchHitApplicationStatus';

type Props = {
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  endDateTime: ApiISODateTimeString | null;
  isPostedAnonymously: boolean;
  location: string | null;
  locationType: ApiListingLocationType | null;
  listingStats: ReactNode[];
  name: string;
  orgName: string | null;
  orgType: string | null;
  showLabel: boolean;
  startDateTime: ApiISODateTimeString | null;
  timezone: string | null;
  promotionRadius: ApiVolopPromotionRadius | null;
};

export function SearchVolopHitInfo({
  application,
  endDateTime,
  isPostedAnonymously,
  location,
  locationType,
  listingStats,
  name,
  orgName,
  orgType,
  showLabel,
  startDateTime,
  timezone,
  promotionRadius,
}: Props) {
  const { i18n } = useI18n();

  const type = 'VOLOP';
  return (
    <SearchHitInfoHolder>
      {showLabel && (
        <div style={{ marginBottom: 14 }}>
          <ListingTypeLabel listingType={type} orgType={null} />
        </div>
      )}

      <SearchHitInfoHeader>
        <SearchHitInfoHeaderHeading>
          <div>
            <span data-qa-id="search-result-link">{name.slice(0, 120)}</span>
            {name.length > 120 && <>&hellip;</>}
          </div>

          {isPostedAnonymously && (
            <Box display="inline-block" ml="4px" verticalAlign="middle">
              <LegacyTooltip width={360}>
                {i18n(
                  `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                )}
              </LegacyTooltip>
            </Box>
          )}
        </SearchHitInfoHeaderHeading>

        {(orgType === 'CORPORATION' || orgName) && (
          <SearchHitInfoHeaderOrgName>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              {orgName}
            </Box>
          </SearchHitInfoHeaderOrgName>
        )}
      </SearchHitInfoHeader>

      <SearchHitInfoBoxListingLabels
        listingType={type}
        location={
          promotionRadius === 'PROMOTED_VOLOP_RADIUS_NWIDE' ? null : location
        }
        locationType={locationType}
        timezone={timezone}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        pillItems={[]}
        salaryFormatted={null}
        jobTypes={[]}
      />

      <SearchHitInfoFooterHolder>
        {listingStats.length > 0 && (
          <SearchHitInfoStats>
            {listingStats.map((info, index) => (
              // oxlint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {index > 0 && <SearchHitInfoPipe />}
                {info}
              </Fragment>
            ))}
          </SearchHitInfoStats>
        )}

        {promotionRadius && (
          <SearchHitInfoPromotedLabel>
            {i18n('FEATURED')}
          </SearchHitInfoPromotedLabel>
        )}

        {application && (
          <SearchHitApplicationStatus
            status={application.status}
            created={application.created}
          />
        )}
      </SearchHitInfoFooterHolder>
    </SearchHitInfoHolder>
  );
}
